import z from "zod";
import { imageWithUploadSchema } from "../../common";
import { dateSchema } from "../../validation";

/**
 * Whose side issued / uploaded the proof of invoice.
 *
 * Broker-era values (`carrier_invoice` / `hero_invoice`) are
 * preserved so legacy docs still parse, but new writes use the
 * role-neutral pair (`buyer_proof` / `seller_proof`) which matches
 * the new bilateral `Invoice.buyer` / `Invoice.seller` shape.
 *
 * - `seller_proof` — issued by the party selling on this invoice
 *   (the receivable holder). Common case for freight invoices.
 * - `buyer_proof`  — issued by the party paying on this invoice.
 *   Used when the buyer sends in a transfer slip or their own
 *   PO-matched invoice.
 * - `other`        — anything not falling into the above two.
 *
 * Legacy values (kept for read compatibility, not for new writes):
 * - `carrier_invoice` — pre-refactor "carrier-side" proof.
 * - `hero_invoice`    — pre-refactor "Hero pays carrier" framing.
 */
export const invoiceProofTargetType = z.enum([
  // New role-neutral values:
  'buyer_proof',
  'seller_proof',
  'other',
  // Legacy values — read-only, not for new writes.
  'carrier_invoice',
  'hero_invoice',
]);
export type InvoiceProofTarget = z.infer<typeof invoiceProofTargetType>;

export const invoiceProofStatusType = z.enum(['pending', 'approved', 'rejected']);
export type InvoiceProofStatus = z.infer<typeof invoiceProofStatusType>;

export const invoiceProofSchema = z.object({
  /**
   * Invoice image as a pdf file.
   *
   * Optional so a create-time shell — `{ refNumber, target,
   * status: 'pending' }` — can land on the invoice the moment a
   * user types an invoice number in the capture flow, before
   * they actually upload the VAT PDF via the upload-proof
   * drawer. The upload mutation later fills `image` +
   * `uploadedAt` on the same `invoiceProof` doc.
   */
  image: imageWithUploadSchema.optional(),

  /**
   * The date in which the invoice was uploaded.
   *
   * Optional alongside `image` for the same create-time shell
   * reason — it gets set when the actual VAT upload happens.
   */
  uploadedAt: dateSchema.optional(),

  /** the reference number of the invoice. */
  refNumber: z.coerce.string().trim(),

  /**
   * Whose side this proof came from. See
   * {@link invoiceProofTargetType} for the value set.
   */
  target: invoiceProofTargetType,

  /** review status set by the finance/admin team */
  status: invoiceProofStatusType,

  /** admin note visible to the uploader (e.g. rejection reason) */
  note: z.coerce.string().trim().optional(),
});

export type InvoiceProof = z.infer<typeof invoiceProofSchema>;